import * as React from "react";
import { useQuery } from "@apollo/client";
import { gettext } from "i18n";
import { GET_ORGANIZATIONS } from "utils/users";
import { GetOrganizations } from "utils/__generated__/GetOrganizations";
import { SearchableDropdown, DropdownOption } from "./SearchableDropdown";

export interface SearchableOrganizationPickerProps {
  onChange: (value: string) => void;
  value: string;
  filter?: (organization: SearchableOrganization) => boolean;
  placeholder?: string;
  useFixedPositioning?: boolean;
  onOrganizationSelect?: (organization: SearchableOrganization | null) => void;
}

export interface SearchableOrganization {
  id: number;
  name: string;
  slug: string;
}

export const SearchableOrganizationPicker: React.FC<
  SearchableOrganizationPickerProps
> = ({
  onChange,
  value,
  filter = () => true,
  placeholder,
  useFixedPositioning = true,
  onOrganizationSelect,
}) => {
  const { data, loading } = useQuery<GetOrganizations>(GET_ORGANIZATIONS);

  const organizations = React.useMemo(() => {
    const orgList = [...(data?.organization ?? [])];
    orgList.sort((a, b) => a.name.localeCompare(b.name));
    return orgList.filter(filter);
  }, [data?.organization, filter]);

  const options: DropdownOption[] = React.useMemo(() => {
    return organizations.map((org) => ({
      value: String(org.id),
      label: org.name,
      sublabel: org.slug,
    }));
  }, [organizations]);

  const handleChange = React.useCallback(
    (selectedValue: string) => {
      onChange(selectedValue);
      if (onOrganizationSelect) {
        const selectedOrg = organizations.find(
          (o) => String(o.id) === selectedValue,
        );
        onOrganizationSelect(selectedOrg ?? null);
      }
    },
    [onChange, onOrganizationSelect, organizations],
  );

  if (loading) {
    return <div>{gettext("Loading organizations...")}</div>;
  }

  if (organizations.length === 0) {
    return <div>{gettext("No organizations available")}</div>;
  }

  return (
    <SearchableDropdown
      placeholder={placeholder ?? gettext("Select organization...")}
      value={value}
      options={options}
      onChange={handleChange}
      useFixedPositioning={useFixedPositioning}
    />
  );
};
